import React, { useMemo, useState, useEffect } from "react";
import { Select, Typography, Space, Divider } from "antd";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import { ResponsivePie } from "@nivo/pie";

const { Text } = Typography;

export default function PipelineChart({ result, height = "60vh" }) {
  /* -------------------- State -------------------- */
  const [chartType, setChartType] = useState("stackedBar");
  const [groupKey, setGroupKey] = useState([]);
  const [valueKeys, setValueKeys] = useState([]);

  const resolveDimensionValue = (val) => {
    if (val == null) return "—";
    if (typeof val !== "object") return String(val);
    return val.name || val.Name || val.label || val.Label || val.value || val.id || "—";
  };

  /* -------------------- Supported Result Types -------------------- */
  const isChartSupported = ["grouped", "mergedJoin", "intersection"].includes(result?.resultType);

  /* -------------------- Detect Fields -------------------- */
  const { dimensionFields, numericFields } = useMemo(() => {
    const rows = result?.results || [];
    if (!rows.length) return { dimensionFields: [], numericFields: [] };

    const sample = rows[0];
    const dimensions = [];
    const numerics = [];

    Object.entries(sample).forEach(([key, value]) => {
      if (key === "_id" || key === "records") return;
      if (typeof value === "number") numerics.push(key);
      else dimensions.push(key);
    });

    return { dimensionFields: dimensions, numericFields: numerics };
  }, [result]);

  /* -------------------- Auto Defaults -------------------- */
  useEffect(() => {
    if (!groupKey.length && dimensionFields.length) setGroupKey([dimensionFields[0]]);
    if (!valueKeys.length && numericFields.length) setValueKeys([numericFields[0]]);
  }, [dimensionFields, numericFields]);

  /* -------------------- Build Chart Data -------------------- */
  const chartData = useMemo(() => {
    if (!isChartSupported || !groupKey?.length || !valueKeys.length) return [];

    const xKey = groupKey[0];          
    const stackKeys = groupKey.slice(1); 
    const numericKeys = valueKeys;       

    // ----------------------------
    // CASE 1: ONLY ONE GROUP KEY
    // ----------------------------
    if (!stackKeys.length) {
      const data = (result?.results || [])
        .map((row) => {
          const xValue = resolveDimensionValue(row[xKey]);
          return {
            group: xValue,
            ...Object.fromEntries(numericKeys.map((k) => [k, Number(row[k] ?? 0)])),
          };
        })
        .filter(row => row.group != null && row.group !== "" && row.group !== "—");

      console.log("📊 chartData (single group):", data);
      return { data, keys: numericKeys };
    }

    // ----------------------------
    // CASE 2: MULTIPLE GROUP KEYS
    // ----------------------------
    const map = {};
    const allStackKeys = new Set();

    (result?.results || []).forEach((row) => {
      const xValue = resolveDimensionValue(row[xKey]);
      if (!xValue || xValue === "—") return;

      if (!map[xValue]) map[xValue] = { group: xValue, _tooltip: {} };

      stackKeys.forEach((stackKey) => {
        const stackValue = resolveDimensionValue(row[stackKey]);
        allStackKeys.add(stackValue);

        numericKeys.forEach((numKey) => {
          const val = Number(row[numKey] ?? 0);
          map[xValue][stackValue] = (map[xValue][stackValue] || 0) + val;
          map[xValue]._tooltip[stackValue] = `${stackKey}: ${stackValue}`;
        });
      });
    });

    const data = Object.values(map);

    console.log("📊 chartData (stacked):", data);
    console.log("🗝️ All stack keys:", Array.from(allStackKeys));

    return { data, keys: Array.from(allStackKeys) };
  }, [isChartSupported, groupKey, valueKeys, result]);

  /* -------------------- Guards -------------------- */
  if (!isChartSupported) return <Text type="secondary">Charts are available only for grouped / merged results</Text>;
  if (!chartData.data?.length) return <Text type="secondary">No chartable data</Text>;

  /* -------------------- Render -------------------- */
  return (
    <>
      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          value={chartType}
          onChange={setChartType}
          style={{ width: 180 }}
          getPopupContainer={(trigger) => trigger.parentElement}
          options={[
            { label: "Stacked Bar", value: "stackedBar" },
            { label: "Grouped Bar", value: "groupedBar" },
            { label: "Line", value: "line" },
            { label: "Pie", value: "pie" },
          ]}
        />

        <Select
          mode="multiple"
          value={groupKey}
          onChange={setGroupKey}
          placeholder="X-Axis (Dimension)"
          style={{ width: 220 }}
          getPopupContainer={(trigger) => trigger.parentElement}
          options={dimensionFields.map((f) => ({ label: f, value: f }))}
        />

        <Select
          mode="multiple"
          value={valueKeys}
          onChange={setValueKeys}
          placeholder="Values (Numeric)"
          style={{ width: 300 }}
          getPopupContainer={(trigger) => trigger.parentElement}
          options={numericFields.map((f) => ({ label: f, value: f }))}
        />
      </Space>

      <Divider />

      <div style={{ height }}>
        {(chartType === "stackedBar" || chartType === "groupedBar") && (
          <ResponsiveBar
            data={chartData.data}
            keys={chartType === "stackedBar" ? chartData.keys : valueKeys}
            indexBy="group"
            groupMode={chartType === "stackedBar" ? "stacked" : "grouped"}
            margin={{ top: 40, right: 140, bottom: 80, left: 60 }}
            padding={0.3}
            colorBy="id"
            colors={{ scheme: "nivo" }}
            axisBottom={{ tickRotation: 30 }}
            enableLabel
            tooltip={({ id, value, indexValue, data }) => (
              <div>
                <strong>{indexValue}</strong><br />
                {id}: {value}<br />
                {data._tooltip?.[id] && <small>{data._tooltip[id]}</small>}
              </div>
            )}
            legends={[
              {
                dataFrom: "keys",
                anchor: "right",
                direction: "column",
                translateX: 120,
                itemHeight: 20,
              },
            ]}
          />
        )}

        {chartType === "line" && (
          <ResponsiveLine
            data={valueKeys.map((k) => ({
              id: k,
              data: chartData.data.map((d) => ({ x: d.group, y: d[k] })),
            }))}
            margin={{ top: 40, right: 140, bottom: 80, left: 60 }}
            axisBottom={{ tickRotation: 30 }}
            useMesh
          />
        )}

        {chartType === "pie" && (
          <ResponsivePie
            data={chartData.data.map((d) => ({
              id: d.group,
              value: valueKeys.reduce((sum, k) => sum + (d[k] || 0), 0),
            }))}
            margin={{ top: 40, right: 40, bottom: 40, left: 40 }}
            innerRadius={0.5}
            padAngle={1}
          />
        )}
      </div>
    </>
  );
}